import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Sparkles } from 'lucide-react'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import { FormCols, FormSection, InfoNote, SwitchField, Textarea } from '../../components/Form'
import FormRow from '../../components/FormRow'
import DatePicker from '../../components/DatePicker'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { getProgramsPage, quickCreateProgram } from './programsService'
import { strings as S } from './strings'
import './QuickProgramPage.css'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  arabicName: '',
  arabicDescription: '',
  englishName: '',
  englishDescription: '',
  pastCoursesView: 'show',
  futureCoursesView: 'show',
  allowAddingCoursesAfterExamination: false,
  topicIds: [],
  /* Every course needs a level, and this wizard does not ask for one — it exists to make a
     programme in three steps. `1` is مبتدئ, the same default the full course form starts on,
     and the only other option is leaving it 0: not a level any row has, so `SaveCourse` was
     refused by `FK_Courses_CourseLevel` and the wizard died after creating the programme
     but before any of its courses. */
  level: '1',
  /* The legacy's own defaults, off the two number inputs. */
  passLimit: 50,
  questionsCount: 5,
  startDate: '',
  endDate: '',
}

const asOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }))

/**
 * إنشاء سريع — Views/ProgramsManagement/_ProgramManagement_Program_Modal_Quick.
 *
 * This flow did not exist in the redesign at all. The legacy has it as a real
 * three-step Bootstrap wizard (`quickProgramStp1..3`) and it is the fastest path
 * through the whole product: it creates a programme, turns each chosen topic
 * into a course, and gives every course an exam from one set of options —
 * work that otherwise takes three screens and a dozen saves.
 *
 * The steps are the legacy's, and so are the fields:
 *
 *   1  the programme        the same seven controls the full form asks for
 *   2  the topics           a checkbox per category, which become the courses
 *   3  the course options   pass limit, question count, and the period they run
 *
 * What is added is the running summary beside step 3: the wizard's whole point
 * is that one form makes many things, and the legacy never says how many.
 */
export default function QuickProgramPage() {
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack('/programs')
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [step, setStep] = useState(0)
  const [topics, setTopics] = useState([])
  const [saving, setSaving] = useState(false)

  /* THE REAL TOPICS, not `src/data/content`.

     This was the last screen still reading the mock file, which is why it offered thirty
     subjects on an installation that has one — and every id in it belongs to a topic that
     does not exist, so a programme built from them would have been built from nothing.
     `getProgramsPage` carries the topic list the course form already uses. */
  useEffect(() => {
    let alive = true
    getProgramsPage()
      .then((page) => {
        if (alive) setTopics(page.categories)
      })
      .catch(() => {
        /* An empty list rather than a half-built one; the step refuses to advance without a
           selection, so it says what is wrong on its own. */
        if (alive) setTopics([])
      })
    return () => {
      alive = false
    }
  }, [])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const toggleTopic = (topicId) =>
    setValues((previous) => ({
      ...previous,
      topicIds: previous.topicIds.includes(topicId)
        ? previous.topicIds.filter((value) => value !== topicId)
        : [...previous.topicIds, topicId],
    }))

  const steps = useMemo(
    () => [
      { id: 'identity', label: S.form.steps.identity },
      { id: 'topics', label: S.form.steps.topics },
      { id: 'options', label: S.form.steps.options },
    ],
    [],
  )

  const validateStep = (index) => {
    const next = {}
    if (index === 0) {
      for (const key of ['arabicName', 'englishName', 'arabicDescription', 'englishDescription']) {
        if (!values[key].trim()) next[key] = S.form.required
      }
    }
    if (index === 1 && values.topicIds.length === 0) next.topicIds = S.form.topicsRequired
    if (index === 2) {
      if (!(values.passLimit >= 1 && values.passLimit <= 100)) next.passLimit = S.form.range
      if (!(values.questionsCount >= 1 && values.questionsCount <= 200))
        next.questionsCount = S.form.range
      if (!values.startDate) next.startDate = S.form.required
      if (!values.endDate) next.endDate = S.form.required
      else if (values.startDate && values.endDate <= values.startDate)
        next.endDate = S.form.dateOrder
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    setSaving(true)
    /* Composed from two endpoints, one course at a time, so a failure part-way is real:
       the count says what was actually created rather than what was asked for. */
    const result = await quickCreateProgram(values)
    if (!result.ok) {
      setSaving(false)
      return toast.error(
        `${result.message || S.actionFailed} — ${result.coursesCreated} ${S.form.topicsChosen}`,
      )
    }
    toast.success(`${serverText(result.message) || S.form.saved} — ${result.coursesCreated} ${S.form.topicsChosen}`)
    navigate('/programs')
  }

  const chosen = values.topicIds.length

  return (
    <FormPage
      icon={Sparkles}
      title={S.form.quickTitle}
      subtitle={S.form.quickSubtitle}
      steps={steps}
      step={step}
      onStep={setStep}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
      aside={
        step > 0 ? (
          <div className="quickprog__tally">
            <p className="quickprog__tally-label">
              <bdi>{S.form.topics}</bdi>
            </p>
            <p className="quickprog__tally-figure tabular">{chosen}</p>
            <p className="quickprog__tally-note">
              <bdi>{S.form.topicsChosen}</bdi>
            </p>
          </div>
        ) : null
      }
    >
      {({ step: current }) => {
        if (current === 0) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.arabicName} error={errors.arabicName}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={values.arabicName}
                      onChange={set('arabicName')}
                      invalid={!!errors.arabicName}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.englishName} error={errors.englishName}>
                  {(id) => (
                    <TextField
                      id={id}
                      dir="ltr"
                      value={values.englishName}
                      onChange={set('englishName')}
                      invalid={!!errors.englishName}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.arabicDescription} error={errors.arabicDescription}>
                  {(id) => (
                    <Textarea
                      id={id}
                      rows={4}
                      value={values.arabicDescription}
                      onChange={set('arabicDescription')}
                      invalid={!!errors.arabicDescription}
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.englishDescription} error={errors.englishDescription}>
                  {(id) => (
                    <Textarea
                      id={id}
                      dir="ltr"
                      rows={4}
                      value={values.englishDescription}
                      onChange={set('englishDescription')}
                      invalid={!!errors.englishDescription}
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.pastCourses}>
                  {(id) => (
                    <Select
                      id={id}
                      value={values.pastCoursesView}
                      onChange={set('pastCoursesView')}
                      options={asOptions(S.form.pastViews)}
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.futureCourses}>
                  {(id) => (
                    <Select
                      id={id}
                      value={values.futureCoursesView}
                      onChange={set('futureCoursesView')}
                      options={asOptions(S.form.futureViews)}
                    />
                  )}
                </FormRow>
              </FormCols>

              <SwitchField
                checked={values.allowAddingCoursesAfterExamination}
                onChange={set('allowAddingCoursesAfterExamination')}
                label={S.form.allowLateCourses}
              />
              {values.allowAddingCoursesAfterExamination && (
                <InfoNote>
                  <bdi>{S.form.allowLateCoursesNote}</bdi>
                </InfoNote>
              )}
            </FormSection>
          )
        }

        if (current === 1) {
          return (
            <FormSection>
              <InfoNote>
                <bdi>{S.form.topicsHint}</bdi>
              </InfoNote>

              <div className="quickprog__tools">
                <button
                  type="button"
                  className="quickprog__tool"
                  onClick={() => set('topicIds')(topics.map((topic) => topic.value))}
                >
                  <bdi>{S.form.selectAll}</bdi>
                </button>
                <button
                  type="button"
                  className="quickprog__tool"
                  onClick={() => set('topicIds')([])}
                >
                  <bdi>{S.form.clearAll}</bdi>
                </button>
              </div>

              {errors.topicIds && (
                <p className="quickprog__error" role="alert">
                  <bdi>{errors.topicIds}</bdi>
                </p>
              )}

              {/* A grid of cards rather than a column of checkboxes: forty
                  topics in one list is a scroll, and the thing being chosen is
                  a subject, which reads better as a tile than as a row. */}
              <ul className="quickprog__topics">
                {topics.map((topic) => {
                  const on = values.topicIds.includes(topic.value)
                  return (
                    <li key={topic.value}>
                      <button
                        type="button"
                        className={`quickprog__topic${on ? ' is-on' : ''}`}
                        onClick={() => toggleTopic(topic.value)}
                        aria-pressed={on}
                      >
                        <span className="quickprog__tick" aria-hidden="true">
                          {on && <Check size={13} strokeWidth={3} />}
                        </span>
                        {/* The count the mock carried is not in this payload, and a number
                            that is not the topic's own is worse than none. */}
                        <bdi className="quickprog__topic-name">{topic.label}</bdi>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </FormSection>
          )
        }

        return (
          <FormSection>
            <FormCols>
              <FormRow label={S.form.passLimit} error={errors.passLimit}>
                {(id) => (
                  <TextField
                    id={id}
                    type="number"
                    min={1}
                    max={100}
                    value={values.passLimit}
                    onChange={(value) => set('passLimit')(Number(value))}
                    invalid={!!errors.passLimit}
                  />
                )}
              </FormRow>
              <FormRow label={S.form.questionsCount} error={errors.questionsCount}>
                {(id) => (
                  <TextField
                    id={id}
                    type="number"
                    min={1}
                    max={200}
                    value={values.questionsCount}
                    onChange={(value) => set('questionsCount')(Number(value))}
                    invalid={!!errors.questionsCount}
                  />
                )}
              </FormRow>
              <FormRow label={S.form.startDate} error={errors.startDate}>
                {(id) => (
                  <DatePicker
                    id={id}
                    value={values.startDate}
                    onChange={set('startDate')}
                    invalid={!!errors.startDate}
                  />
                )}
              </FormRow>
              <FormRow label={S.form.endDate} error={errors.endDate}>
                {(id) => (
                  <DatePicker
                    id={id}
                    value={values.endDate}
                    onChange={set('endDate')}
                    invalid={!!errors.endDate}
                  />
                )}
              </FormRow>
            </FormCols>
          </FormSection>
        )
      }}
    </FormPage>
  )
}
